import { FormEvent, useEffect, useMemo, useState } from 'react';
import { Disc3, Headphones, ListMusic, Music2, Plus, QrCode, Trash2, UploadCloud } from 'lucide-react';
import QRCode from 'qrcode';
import {
  AudioguideItem,
  PodcastPlaylist,
  createAudioguide,
  createPlaylist,
  deleteAudioguide,
  deletePlaylist,
  listAudioguideCatalog,
  updatePlaylist,
  updateAudioguide,
} from '../services/audioguidesService';
import { uploadFile } from '../services/uploadService';
import { getExperiences } from '../services/legacyApiService';
import { LegacyExperience } from '../types/legacy';

type EpisodeFormState = {
  title: string;
  description: string;
  playlistId: string;
  isPublished: boolean;
};

type PlaylistFormState = {
  name: string;
  description: string;
  experienceId: string;
  isPublished: boolean;
};

const EMPTY_EPISODE_FORM: EpisodeFormState = {
  title: '',
  description: '',
  playlistId: '',
  isPublished: true,
};

const EMPTY_PLAYLIST_FORM: PlaylistFormState = {
  name: '',
  description: '',
  experienceId: '',
  isPublished: true,
};

const Audioguides = () => {
  const [rows, setRows] = useState<AudioguideItem[]>([]);
  const [playlists, setPlaylists] = useState<PodcastPlaylist[]>([]);
  const [experiences, setExperiences] = useState<LegacyExperience[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [experienceFilter, setExperienceFilter] = useState<string>('all');
  const [episodeForm, setEpisodeForm] = useState<EpisodeFormState>(EMPTY_EPISODE_FORM);
  const [playlistForm, setPlaylistForm] = useState<PlaylistFormState>(EMPTY_PLAYLIST_FORM);
  const [audioFile, setAudioFile] = useState<File | null>(null);
  const [episodeCoverFile, setEpisodeCoverFile] = useState<File | null>(null);
  const [playlistCoverFile, setPlaylistCoverFile] = useState<File | null>(null);
  const [editingEpisode, setEditingEpisode] = useState<AudioguideItem | null>(null);
  const [editingPlaylist, setEditingPlaylist] = useState<PodcastPlaylist | null>(null);

  const selectedPlaylist = useMemo(
    () => playlists.find((item) => item.id === episodeForm.playlistId) || null,
    [playlists, episodeForm.playlistId],
  );
  const selectedExperience = useMemo(
    () => experiences.find((item) => String(item.id) === playlistForm.experienceId) || null,
    [experiences, playlistForm.experienceId],
  );
  const filteredPlaylists = useMemo(
    () => (experienceFilter === 'all'
      ? playlists
      : playlists.filter((item) => String(item.experienceId || '') === experienceFilter)),
    [experienceFilter, playlists],
  );
  const filteredRows = useMemo(
    () => (experienceFilter === 'all'
      ? rows
      : rows.filter((item) => String(item.experienceId || '') === experienceFilter)),
    [experienceFilter, rows],
  );
  const episodeTitle = useMemo(
    () => (editingEpisode ? `Modifica episodio: ${editingEpisode.title}` : 'Nuovo episodio podcast'),
    [editingEpisode],
  );
  const playlistTitle = useMemo(
    () => (editingPlaylist ? `Modifica playlist: ${editingPlaylist.name}` : 'Nuova playlist'),
    [editingPlaylist],
  );

  const refresh = async () => {
    setLoading(true);
    setError(null);
    try {
      const [catalog, experienceRows] = await Promise.all([
        listAudioguideCatalog(),
        getExperiences(),
      ]);
      const withQr = await Promise.all(
        catalog.episodes.map(async (item) => {
          const link = item.deepLink || item.publicUrl;
          const qrDataUrl = link
            ? await QRCode.toDataURL(link, { width: 300, margin: 1 })
            : '';
          return { ...item, qrDataUrl };
        }),
      );
      setRows(withQr);
      setPlaylists(catalog.playlists);
      setExperiences(experienceRows);
      setExperienceFilter((prev) => (
        prev === 'all' || experienceRows.some((item) => String(item.id) === prev)
          ? prev
          : 'all'
      ));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore caricamento audioguide');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void refresh();
  }, []);

  const resetForm = () => {
    setEditingEpisode(null);
    setEpisodeForm(EMPTY_EPISODE_FORM);
    setAudioFile(null);
    setEpisodeCoverFile(null);
  };

  const resetPlaylistForm = () => {
    setEditingPlaylist(null);
    setPlaylistForm(EMPTY_PLAYLIST_FORM);
    setPlaylistCoverFile(null);
  };

  const onSubmitEpisode = async (event: FormEvent) => {
    event.preventDefault();
    const playlistName = String(selectedPlaylist?.name || '').trim();
    if (!episodeForm.title.trim() || !episodeForm.playlistId || !playlistName) {
      setError('Titolo episodio e playlist sono obbligatori.');
      return;
    }
    const selectedPlaylistItem = selectedPlaylist;
    if (!selectedPlaylistItem) {
      setError('Playlist selezionata non valida.');
      return;
    }
    if (!editingEpisode && (!audioFile || !episodeCoverFile)) {
      setError('Per un nuovo episodio carichi audio e copertina episodio.');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const entityKey = editingEpisode?.id || `audio_${Date.now().toString(36)}`;
      const audioUrl = audioFile
        ? await uploadFile(audioFile, 'media', entityKey)
        : (editingEpisode?.audioUrl || '');
      const episodeCoverUrl = episodeCoverFile
        ? await uploadFile(episodeCoverFile, 'media', entityKey)
        : (editingEpisode?.episodeCoverUrl || editingEpisode?.coverUrl || '');
      const playlistCoverUrl = playlistCoverFile
        ? await uploadFile(playlistCoverFile, 'media', `playlist_${selectedPlaylistItem.id}`)
        : (selectedPlaylistItem.coverUrl || editingEpisode?.playlistCoverUrl || '');
      const experienceId = String(selectedPlaylistItem.experienceId || '').trim();
      const experienceName = String(selectedPlaylistItem.experienceName || '').trim();

      if (!audioUrl || !episodeCoverUrl) {
        throw new Error('Audio e copertina episodio sono obbligatori.');
      }
      if (!experienceId || !experienceName) {
        throw new Error('La playlist selezionata non è associata a un’esperienza valida.');
      }

      if (editingEpisode) {
        await updateAudioguide(editingEpisode.id, {
          title: episodeForm.title.trim(),
          description: episodeForm.description.trim(),
          playlistId: selectedPlaylistItem.id,
          playlistName,
          experienceId,
          experienceName,
          isPublished: Boolean(episodeForm.isPublished),
          audioUrl,
          episodeCoverUrl,
          playlistCoverUrl,
        });
      } else {
        await createAudioguide({
          title: episodeForm.title.trim(),
          description: episodeForm.description.trim(),
          playlistId: selectedPlaylistItem.id,
          playlistName,
          experienceId,
          experienceName,
          isPublished: Boolean(episodeForm.isPublished),
          audioUrl,
          episodeCoverUrl,
          playlistCoverUrl,
        });
      }

      resetForm();
      if (playlistCoverFile && selectedPlaylistItem.id) {
        await updatePlaylist(selectedPlaylistItem.id, { coverUrl: playlistCoverUrl });
      }
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Salvataggio non riuscito');
    } finally {
      setSaving(false);
    }
  };

  const onSubmitPlaylist = async (event: FormEvent) => {
    event.preventDefault();
    const experienceId = String(playlistForm.experienceId || '').trim();
    const experienceName = String(selectedExperience?.name || '').trim();
    if (!playlistForm.name.trim() || !experienceId || !experienceName) {
      setError('Nome playlist ed esperienza sono obbligatori.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const playlistCoverUrl = playlistCoverFile
        ? await uploadFile(
          playlistCoverFile,
          'media',
          `playlist_${playlistForm.name.trim().replace(/\s+/g, '_').toLowerCase()}`,
        )
        : (editingPlaylist?.coverUrl || '');

      if (editingPlaylist?.id) {
        await updatePlaylist(editingPlaylist.id, {
          name: playlistForm.name.trim(),
          description: playlistForm.description.trim(),
          isPublished: Boolean(playlistForm.isPublished),
          experienceId,
          experienceName,
          coverUrl: playlistCoverUrl || undefined,
        });
      } else {
        await createPlaylist({
          name: playlistForm.name.trim(),
          description: playlistForm.description.trim(),
          isPublished: Boolean(playlistForm.isPublished),
          experienceId,
          experienceName,
          coverUrl: playlistCoverUrl || undefined,
        });
      }
      resetPlaylistForm();
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Salvataggio playlist non riuscito');
    } finally {
      setSaving(false);
    }
  };

  const onEditEpisode = (item: AudioguideItem) => {
    setEditingEpisode(item);
    const matchedPlaylist = playlists.find((playlist) =>
      playlist.id === item.playlistId
      || playlist.name === item.playlistName
      || playlist.name === item.playlist,
    ) || null;
    setEpisodeForm({
      title: item.title || '',
      description: item.description || '',
      playlistId: matchedPlaylist?.id || '',
      isPublished: Boolean(item.isPublished),
    });
    setPlaylistCoverFile(null);
    setAudioFile(null);
    setEpisodeCoverFile(null);
  };

  const onEditPlaylist = (item: PodcastPlaylist) => {
    setEditingPlaylist(item);
    setPlaylistForm({
      name: item.name || '',
      description: String(item.description || ''),
      experienceId: String(item.experienceId || ''),
      isPublished: Boolean(item.isPublished),
    });
    setPlaylistCoverFile(null);
  };

  const onDeleteEpisode = async (id: string) => {
    if (!window.confirm('Eliminare questo episodio?')) return;
    try {
      await deleteAudioguide(id);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Eliminazione non riuscita');
    }
  };

  const onDeletePlaylist = async (id: string) => {
    const hasEpisodes = rows.some((item) => item.playlistId === id);
    if (hasEpisodes) {
      setError('Questa playlist contiene episodi: elimini prima gli episodi associati.');
      return;
    }
    if (!window.confirm('Eliminare questa playlist?')) return;
    try {
      await deletePlaylist(id);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Eliminazione playlist non riuscita');
    }
  };

  return (
    <div className="space-y-6">
      <div className="rounded-2xl p-6 bg-gradient-to-r from-[#1f2937] via-[#111827] to-[#0f172a] text-white">
        <div className="flex items-center justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-white/70">Audio Experience</p>
            <h1 className="text-2xl font-semibold flex items-center gap-2 mt-1">
              <Headphones size={24} />
              Audioguide & Podcast
            </h1>
            <p className="text-sm text-white/80 mt-2">
              Carichi audio e copertina su Firebase, poi genera subito un QR che apre Glooci sul podcast.
            </p>
          </div>
          <button
            type="button"
            className="btn-primary inline-flex items-center gap-2"
            onClick={resetForm}
          >
            <Plus size={16} />
            Nuova audioguida
          </button>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-border p-4">
        <div className="flex flex-col md:flex-row md:items-center gap-3">
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-text-primary">Perimetro audioguide</p>
            <p className="text-xs text-text-secondary mt-1">
              Ogni amministratore può vedere e gestire solo playlist ed episodi associati alle esperienze che gli sono accessibili.
            </p>
          </div>
          <div className="w-full md:w-72">
            <select
              className="w-full border rounded-lg px-3 py-2 bg-white"
              value={experienceFilter}
              onChange={(e) => setExperienceFilter(e.target.value)}
            >
              <option value="all">Tutte le esperienze accessibili</option>
              {experiences.map((experience) => (
                <option key={experience.id} value={String(experience.id)}>
                  {experience.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="grid xl:grid-cols-2 gap-6">
        <form className="bg-white rounded-2xl border border-border p-5 space-y-4" onSubmit={onSubmitPlaylist}>
          <h2 className="text-lg font-semibold text-text-primary flex items-center gap-2">
            <ListMusic size={18} />
            {playlistTitle}
          </h2>
          <label className="space-y-1 block">
            <span className="text-xs text-text-secondary">Nome playlist</span>
            <input
              className="w-full border rounded-lg px-3 py-2"
              value={playlistForm.name}
              onChange={(e) => setPlaylistForm((p) => ({ ...p, name: e.target.value }))}
              placeholder="Es. Musei di Napoli"
            />
          </label>
          <label className="space-y-1 block">
            <span className="text-xs text-text-secondary">Esperienza associata</span>
            <select
              className="w-full border rounded-lg px-3 py-2 bg-white"
              value={playlistForm.experienceId}
              onChange={(e) => setPlaylistForm((p) => ({ ...p, experienceId: e.target.value }))}
            >
              <option value="">Seleziona esperienza</option>
              {experiences.map((experience) => (
                <option key={experience.id} value={String(experience.id)}>
                  {experience.name}
                </option>
              ))}
            </select>
          </label>
          <label className="space-y-1 block">
            <span className="text-xs text-text-secondary">Descrizione playlist</span>
            <textarea
              className="w-full border rounded-lg px-3 py-2 min-h-[96px]"
              value={playlistForm.description}
              onChange={(e) => setPlaylistForm((p) => ({ ...p, description: e.target.value }))}
              placeholder="Descrizione generale della playlist"
            />
          </label>
          <label className="space-y-1 block">
            <span className="text-xs text-text-secondary">Copertina playlist</span>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => setPlaylistCoverFile(e.target.files?.[0] || null)}
              className="w-full border rounded-lg px-3 py-2"
            />
          </label>
          <label className="inline-flex items-center gap-2 text-sm text-text-primary">
            <input
              type="checkbox"
              checked={playlistForm.isPublished}
              onChange={(e) => setPlaylistForm((p) => ({ ...p, isPublished: e.target.checked }))}
            />
            Playlist pubblicata
          </label>
          <div className="flex items-center gap-2">
            <button type="submit" className="btn-primary inline-flex items-center gap-2" disabled={saving}>
              <UploadCloud size={16} />
              {saving ? 'Salvataggio...' : editingPlaylist ? 'Aggiorna playlist' : 'Crea playlist'}
            </button>
            {editingPlaylist && (
              <button type="button" className="btn-outline" onClick={resetPlaylistForm}>
                Annulla
              </button>
            )}
          </div>
        </form>

        <form className="bg-white rounded-2xl border border-border p-5 space-y-4" onSubmit={onSubmitEpisode}>
          <h2 className="text-lg font-semibold text-text-primary flex items-center gap-2">
            <Disc3 size={18} />
            {episodeTitle}
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            <label className="space-y-1">
              <span className="text-xs text-text-secondary">Titolo episodio</span>
              <input
                className="w-full border rounded-lg px-3 py-2"
                value={episodeForm.title}
                onChange={(e) => setEpisodeForm((p) => ({ ...p, title: e.target.value }))}
                placeholder="Podcast del museo..."
              />
            </label>
            <label className="space-y-1">
              <span className="text-xs text-text-secondary">Playlist</span>
              <select
                className="w-full border rounded-lg px-3 py-2 bg-white"
                value={episodeForm.playlistId}
                onChange={(e) => setEpisodeForm((p) => ({ ...p, playlistId: e.target.value }))}
              >
                <option value="">{playlists.length > 0 ? 'Seleziona playlist' : 'Prima crea una playlist'}</option>
                {playlists.map((playlist) => (
                  <option key={playlist.id} value={playlist.id}>
                    {playlist.experienceName ? `${playlist.experienceName} · ${playlist.name}` : playlist.name}
                  </option>
                ))}
              </select>
            </label>
          </div>
          {selectedPlaylist?.experienceName && (
            <div className="rounded-lg border border-border bg-background/40 px-3 py-2 text-xs text-text-secondary">
              Episodio associato a: <span className="font-medium text-text-primary">{selectedPlaylist.experienceName}</span>
            </div>
          )}
          <label className="space-y-1 block">
            <span className="text-xs text-text-secondary">Descrizione episodio</span>
            <textarea
              className="w-full border rounded-lg px-3 py-2 min-h-[96px]"
              value={episodeForm.description}
              onChange={(e) => setEpisodeForm((p) => ({ ...p, description: e.target.value }))}
              placeholder="Descrizione episodio"
            />
          </label>
          <div className="grid md:grid-cols-3 gap-4">
            <label className="space-y-1 block">
              <span className="text-xs text-text-secondary">File audio</span>
              <input
                type="file"
                accept="audio/*"
                onChange={(e) => setAudioFile(e.target.files?.[0] || null)}
                className="w-full border rounded-lg px-3 py-2"
              />
            </label>
            <label className="space-y-1 block">
              <span className="text-xs text-text-secondary">Copertina episodio</span>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => setEpisodeCoverFile(e.target.files?.[0] || null)}
                className="w-full border rounded-lg px-3 py-2"
              />
            </label>
            <label className="space-y-1 block">
              <span className="text-xs text-text-secondary">Aggiorna copertina playlist (opzionale)</span>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => setPlaylistCoverFile(e.target.files?.[0] || null)}
                className="w-full border rounded-lg px-3 py-2"
              />
            </label>
          </div>
          <label className="inline-flex items-center gap-2 text-sm text-text-primary">
            <input
              type="checkbox"
              checked={episodeForm.isPublished}
              onChange={(e) => setEpisodeForm((p) => ({ ...p, isPublished: e.target.checked }))}
            />
            Episodio pubblicato
          </label>
          <div className="flex items-center gap-2">
            <button type="submit" className="btn-primary inline-flex items-center gap-2" disabled={saving}>
              <UploadCloud size={16} />
              {saving ? 'Salvataggio...' : editingEpisode ? 'Aggiorna episodio' : 'Crea episodio'}
            </button>
            {editingEpisode && (
              <button type="button" className="btn-outline" onClick={resetForm}>
                Annulla
              </button>
            )}
          </div>
        </form>
      </div>

      {error && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
          {error}
        </div>
      )}

      <div className="space-y-3">
        <h3 className="text-lg font-semibold text-text-primary flex items-center gap-2">
          <ListMusic size={18} />
          Catalogo playlist
        </h3>
        {filteredPlaylists.length === 0 ? (
          <div className="bg-white border border-border rounded-xl p-5 text-text-secondary">
            Nessuna playlist ancora presente.
          </div>
        ) : (
          <div className="grid lg:grid-cols-2 gap-4">
            {filteredPlaylists.map((item) => (
              <div key={item.id} className="bg-white border border-border rounded-2xl p-4 flex gap-4">
                <img
                  src={item.coverUrl || ''}
                  alt={item.name}
                  className="w-24 h-24 rounded-xl object-cover bg-gray-100"
                />
                <div className="flex-1 min-w-0">
                  <h4 className="font-semibold text-text-primary truncate">{item.name}</h4>
                  <p className="text-xs mt-1 text-primary">{item.experienceName || 'Esperienza non assegnata'}</p>
                  <p className="text-sm text-text-secondary line-clamp-2 mt-1">{item.description || '-'}</p>
                  <p className="text-xs mt-1 text-text-secondary">{item.isPublished ? 'Pubblicata' : 'Bozza'}</p>
                  <div className="flex items-center gap-2 mt-3">
                    <button type="button" className="btn-outline text-xs" onClick={() => onEditPlaylist(item)}>
                      Modifica
                    </button>
                    <button
                      type="button"
                      className="btn-outline text-xs inline-flex items-center gap-1 text-red-600"
                      onClick={() => void onDeletePlaylist(item.id)}
                    >
                      <Trash2 size={14} />
                      Elimina
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="space-y-3">
        <h3 className="text-lg font-semibold text-text-primary flex items-center gap-2">
          <Music2 size={18} />
          Catalogo episodi
        </h3>
        {loading ? (
          <div className="bg-white rounded-2xl border border-border p-5 text-text-secondary">Caricamento...</div>
        ) : filteredRows.length === 0 ? (
          <div className="bg-white rounded-2xl border border-border p-5 text-text-secondary">
            Nessun episodio ancora presente.
          </div>
        ) : (
          <div className="grid lg:grid-cols-2 gap-4">
            {filteredRows.map((item) => (
              <div key={item.id} className="bg-white border border-border rounded-2xl p-4 flex gap-4">
                <img
                  src={item.episodeCoverUrl || item.playlistCoverUrl || item.coverUrl || ''}
                  alt={item.title}
                  className="w-24 h-24 rounded-xl object-cover bg-gray-100"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-text-secondary uppercase tracking-wide">{item.playlistName || item.playlist || 'Playlist'}</p>
                  <p className="text-xs mt-1 text-primary">{item.experienceName || 'Esperienza non assegnata'}</p>
                  <h4 className="font-semibold text-text-primary truncate">{item.title}</h4>
                  <p className="text-sm text-text-secondary line-clamp-2 mt-1">{item.description || '-'}</p>
                  <div className="text-xs text-text-secondary mt-2 truncate">Deep link: {item.deepLink || '-'}</div>
                  <div className="flex items-center gap-2 mt-3">
                    <button type="button" className="btn-outline text-xs" onClick={() => onEditEpisode(item)}>
                      Modifica
                    </button>
                    <button
                      type="button"
                      className="btn-outline text-xs inline-flex items-center gap-1 text-red-600"
                      onClick={() => void onDeleteEpisode(item.id)}
                    >
                      <Trash2 size={14} />
                      Elimina
                    </button>
                  </div>
                </div>
                <div className="w-24 shrink-0 text-center">
                  <div className="text-[11px] text-text-secondary mb-1 inline-flex items-center gap-1">
                    <QrCode size={12} />
                    QR
                  </div>
                  {item.qrDataUrl ? (
                    <img src={item.qrDataUrl} alt={`QR ${item.title}`} className="w-24 h-24 rounded-lg border" />
                  ) : (
                    <div className="w-24 h-24 rounded-lg border bg-gray-50" />
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Audioguides;
